import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import dayjs from 'dayjs';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import DashboardService from 'api/dashboard/DashboardService';
import { LocationAlarmDashboardDto } from 'api/dashboard/dtos/LocationAlarmDashboardDto';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useInfiniteReveal from '../components/useInfiniteReveal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';
import styles from './LocationAlarms.module.scss';

const PAGE_SIZE = 10;

type PeriodOption = 'week' | 'month' | 'custom';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const LocationAlarms = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<LocationAlarmDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [period, setPeriod] = useState<PeriodOption>('week');
	const { dateFrom: customFrom, dateTo: customTo, setDateRange } = useWidgetFilters('locationAlarms');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const getDateRange = () => {
		if (period === 'custom') {
			return { dateFrom: customFrom, dateTo: customTo };
		}
		const daysBack = period === 'week' ? 7 : 30;
		return {
			dateFrom: dayjs().subtract(daysBack, 'day').startOf('day').toDate(),
			dateTo: dayjs().endOf('day').toDate(),
		};
	};

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const range = getDateRange();
				const criteria = {
					...(props.filters || {}),
					...vehicleScope,
					dateFrom: range.dateFrom,
					dateTo: range.dateTo,
				};
				const result = await DashboardService.getLocationAlarms(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, period, customFrom, customTo]);

	const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPeriod(e.target.value as PeriodOption);
	};

	const renderOptions = () => (
		<div className={styles.optionsRow}>
			<select
				className={styles.periodSelect}
				value={period}
				onChange={handlePeriodChange}
			>
				<option value="week">{t('dashboard.alarm_period_week' as any)}</option>
				<option value="month">{t('dashboard.alarm_period_month' as any)}</option>
				<option value="custom">{t('dashboard.alarm_period_custom' as any)}</option>
			</select>
			{period === 'custom' && (
				<WidgetMonthPicker dateFrom={customFrom} dateTo={customTo} onChange={setDateRange} />
			)}
		</div>
	);

	const { visibleItems: pageItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const getAlarmTypeLabel = (type: string | null) => {
		if (!type) return '—';
		return t(`alarms.types.${type}` as any, { defaultValue: type }) as string;
	};

	const buildVehicleTooltip = (item: LocationAlarmDashboardDto) =>
		[item.vehicleBrand, item.vehicleModel].filter(Boolean).join(' ') || undefined;

	const renderLocation = (item: LocationAlarmDashboardDto) => {
		if (item.latitude == null || item.longitude == null) {
			return <span className={styles.muted}>—</span>;
		}
		const lat = item.latitude.toFixed(4);
		const lng = item.longitude.toFixed(4);
		const url = `https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`;
		return (
			<a
				className={styles.mapLink}
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				onClick={e => e.stopPropagation()}
			>
				{lat}, {lng}
			</a>
		);
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.location_alarms') as string}
			subTitleTooltip={t('dashboard.location_alarms_tooltip' as any) as string}
			renderOptions={renderOptions()}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.columns.alarm_date_time' as any)}</th>
									<th>{t('dashboard.columns.vehicle')}</th>
									<th>{t('dashboard.columns.alarm_type' as any)}</th>
									<th>{t('dashboard.columns.location' as any)}</th>
									<th>{t('dashboard.columns.driver' as any)}</th>
								</tr>
							</thead>
							<tbody>
								{pageItems.map((item, index) => (
									<tr
										key={`${item.alarmId}-${item.vehicleId}-${index}`}
										className={styles.tableRow}
										onClick={() => item.alarmId && window.open(`/alarms/details/${item.alarmId}`, '_blank')}
									>
										<td>{dayjs(item.triggerDate).format('DD/MM HH:mm')}</td>
										<td>{item.vehicleRegistrationNumber
											? <span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="location-alarms-vehicle-tooltip" data-place="right">{item.vehicleRegistrationNumber}</span></span>
											: <span className={styles.muted}>—</span>}</td>
										<td>{getAlarmTypeLabel(item.alarmType)}</td>
										<td>{renderLocation(item)}</td>
										<td>{item.driverName || <span className={styles.muted}>—</span>}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={5} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="location-alarms-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default LocationAlarms;
